import 'reflect-metadata';

import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { BASE } from 'common/src/routes/BaseRoutes';

import AuthProvider from './auth/AuthProvider';
import Authenticated from './layouts/Authenticated';
import ProductRoutes from './navigation/ProductRoutes';
import BillImage from './pages/BillImage';
import Error from './pages/Error';
import StatementImage from './pages/StatementImage';
import ChangeExpiredPassword from './pages/auth/ChangeExpiredPassword';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import ResetPassword from './pages/auth/ResetPassword/ResetPassword';
import { SSO } from './pages/auth/SSO';
import VerifyEmail from './pages/auth/VerifyEmail';
import Welcome from './pages/auth/Welcome';
import { LuzmoProvider } from './providers/LuzmoProvider';
import AppThemeProvider from './theme/AppThemeProvider';
import { stylesProvider } from './utils/Snackbar/StylesProvider';

const router = createBrowserRouter([
  {
    element: <Outlet />,
    errorElement: <Error />,
    children: [
      // Authentication routes
      {
        path: BASE.AUTH.CHANGE_EXPIRED_PASSWORD,
        element: <ChangeExpiredPassword />,
      },
      { path: BASE.AUTH.FORGOT_PASSWORD, element: <ForgotPassword /> },
      { path: BASE.AUTH.LOGIN, element: <Login /> },
      { path: BASE.AUTH.RESET_PASSWORD, element: <ResetPassword /> },
      { path: BASE.AUTH.SSO, element: <SSO /> },
      { path: BASE.AUTH.VERIFY_EMAIL, element: <VerifyEmail /> },
      { path: BASE.AUTH.WELCOME, element: <Welcome /> },

      // Authenticated routes
      { element: <Authenticated />, children: ProductRoutes() },

      // Image routes
      { path: BASE.IMAGES.BILL, element: <BillImage /> },
      { path: BASE.IMAGES.STATEMENT, element: <StatementImage /> },
    ],
  },
]);

export default function App() {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: 3, refetchOnWindowFocus: false } },
    queryCache: new QueryCache({
      onError: (error) => {
        Logger.captureError(error as Error);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        Logger.captureError(error as Error);
      },
    }),
  });

  return (
    <AppThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LuzmoProvider>
            <SnackbarProvider
              maxSnack={3}
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              Components={stylesProvider}
            >
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <RouterProvider router={router} />
              </LocalizationProvider>
            </SnackbarProvider>
          </LuzmoProvider>
        </AuthProvider>
      </QueryClientProvider>
    </AppThemeProvider>
  );
}
